import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Edit, Plus, ChevronDown, ChevronRight, Lightbulb, Sparkles, Wand2, Check, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { evaluateAgentComplexity } from '../../utils/agentComplexity';
import toast from 'react-hot-toast';
import ConditionBuilder from './ConditionBuilder';
import { StepType, WorkflowStep, Condition, Agent, StepIntent, SessionConfig, getApiStepType, getStepIntent, AllowedStepIntentOption, AgentTemplate, OutputType } from '../../types';
import IntentAwareStepSelector from './IntentAwareStepSelector';
import StepIntentSelector from './StepIntentSelector';
import SessionConfiguration from './SessionConfiguration';
import { useFlowStore, useProjectStore } from '../../store'; // Import useFlowStore and useProjectStore
import { useModels } from '../../hooks/queries/useModels';
import { useCreateAgent, useUpdateAgent } from '../../hooks/queries/useAgentMutations';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../hooks/queries/queryKeys';
import AgentFormModal from '../agents/AgentFormModal';
import { AgentTemplateSelector } from '../agents/AgentTemplateSelector';
import { CreateAgentForm } from '../../types';
import apiService from '../../services/api';
import { hintDefaultsService } from '../../services/hintDefaults';
import SchemaForm from '../common/SchemaForm';
import { transformModelProperties } from '../../utils/propertyTransformer';
import QueueReadStepConfig from '../Queue/QueueReadStepConfig';
import QueueWriteStepConfig from '../Queue/QueueWriteStepConfig';
import FilterStepConfig from './FilterStepConfig';
import RDAPStepConfig from './RDAPStepConfig';
import { resolveUpstreamForIntrospection } from './upstreamIntrospection';
import ContextualHelpIcon from '../help/ContextualHelpIcon';
import CustomSelect from '../common/CustomSelect';
import ModelPickerField from '../common/ModelPickerField';
import Tooltip from '../common/Tooltip';
import InlineModelCreator from '../agents/InlineModelCreator';
import ModelSchemaPreview from '../agents/ModelSchemaPreview';

interface StepConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  stepType?: StepType;
  restrictStepType?: boolean;
  prevStepType?: StepType;
  agents: import('../../types').Agent[];
  existingStep?: WorkflowStep;
  isEditing?: boolean;
  previousNodeId?: string; // New prop to pass the ID of the node this new step will attach to
  onConvertToAutoPrompt?: (stepId: string) => void; // Callback to convert user_prompt to auto_prompt
  /**
   * AC-1293: Callback to convert a conditional step to a filter step. Wired
   * through to ConditionBuilder's advisor banner CTA so the banner's
   * Convert-to-Filter action runs the same WorkflowEditor handler used by
   * the node context menu (toast + React Flow node swap + modal reopen).
   */
  onConvertToFilter?: (stepId: string) => Promise<void> | void;
}

// Add hint types to match backend
interface FeedbackLoopHint {
  enhancement_instruction?: string;
  validation_rules?: string;
  focus_areas?: string;
  leniency?: 'strict' | 'moderate' | 'generous';
  iteration_context?: string;
}

interface StepHints {
  feedback_loop_hints?: Record<string, FeedbackLoopHint>;
  general_hints?: Record<string, any>;
}

// Default validator hints matching backend
const DEFAULT_VALIDATOR_HINTS: Record<string, FeedbackLoopHint> = {
  '1': {
    enhancement_instruction: "Evaluate the output against the requirements. Provide a balanced assessment with specific, actionable feedback for any areas needing improvement.",
    validation_rules: "Be specific and constructive. Focus on what needs improvement and how to achieve it.",
    leniency: 'strict'
  },
  '2': {
    enhancement_instruction: "This is iteration 2. The output has been revised based on your previous feedback. Assess the improvements that have been made. If you provided a score previously, consider whether the changes warrant an improved score.",
    validation_rules: "IMPORTANT: Acknowledge improvements where they exist. Your assessment should reflect positive progress if changes address your previous feedback. Focus remaining critique only on significant unresolved issues. Your scoring should improve if the output has meaningfully addressed your feedback.",
    focus_areas: "Previous feedback was: {previous_feedback}. Check if these specific concerns have been addressed.",
    leniency: 'moderate'
  },
  '3+': {
    enhancement_instruction: "This is iteration {iteration}. Multiple revisions have been made based on feedback. Evaluate with consideration for the iterative improvements.",
    validation_rules: "IMPORTANT: Your evaluation should heavily weight the cumulative improvements made. If the output substantially addresses the requirements, your assessment should be predominantly positive. Reserve critical feedback only for issues that fundamentally compromise the output's usability.",
    focus_areas: "Previous feedback: {previous_feedback}. Assess whether this has been reasonably addressed.",
    leniency: 'generous',
    iteration_context: "Consider that perfection may not be achievable or necessary. Focus your evaluation on whether the output is sufficiently good for its intended purpose."
  }
};

// Default feedback receiver hints matching backend
const DEFAULT_FEEDBACK_RECEIVER_HINTS: Record<string, FeedbackLoopHint> = {
  '1': {
    enhancement_instruction: "Generate content according to the requirements. Focus on completeness and quality.",
    validation_rules: "Ensure your output addresses all specified requirements comprehensively."
  },
  '2': {
    enhancement_instruction: "This is iteration 2. You have received feedback on your previous output. Use this feedback to improve your response.",
    focus_areas: "Previous feedback was: {previous_feedback}. Address these specific points in your revised output.",
    validation_rules: "IMPORTANT: Your revised output should directly address the feedback provided. Make meaningful improvements based on the specific suggestions."
  },
  '3+': {
    enhancement_instruction: "This is iteration {iteration}. You have received multiple rounds of feedback.",
    focus_areas: "Latest feedback: {previous_feedback}. Focus on addressing any remaining issues.",
    validation_rules: "Your output should demonstrate clear improvements based on all previous feedback. Ensure all major concerns have been addressed.",
    iteration_context: "You have made multiple revisions. Focus on resolving any critical remaining issues while maintaining the improvements already made."
  }
};

// Utility to normalize conditions (string or object) to {field, operator, value} objects
function normalizeConditions(rawConditions: any[]): Condition[] {
  if (!rawConditions || !Array.isArray(rawConditions)) return [];
  return rawConditions.map((cond: any) => {
    if (typeof cond === 'object' && 'field' in cond) {
      return cond;
    }
    if (typeof cond === 'string') {
      const trimmedCond = cond.trim();
      if (!trimmedCond) return { field: '', operator: '', value: '' };
      const operators = ['==', '!=', '>=', '<=', '>', '<'];
      for (const op of operators) {
        const idx = trimmedCond.indexOf(op);
        if (idx !== -1) {
          const field = trimmedCond.slice(0, idx).trim();
          const value = trimmedCond.slice(idx + op.length).trim().replace(/^['"]|['"]$/g, '');
          return { field, operator: op, value };
        }
      }
    }
    return { field: '', operator: '', value: '' };
  });
}

const StepConfigurationModal: React.FC<StepConfigurationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  stepType: initialStepType,
  restrictStepType = false,
  prevStepType,
  agents,
  existingStep,
  isEditing = false,
  previousNodeId,
  onConvertToAutoPrompt,
  onConvertToFilter,
}) => {
  const { nodes, edges, setNodes } = useFlowStore();
  // AC-1412 / AC-1377 P5.1: project models flow through React Query
  // (`useModels`); agent CRUD flows through `useCreateAgent` / `useUpdateAgent`
  // (which invalidate `projectAgents(projectId)` on success). The previous
  // `projectAgents` / `loadAgents` destructure was unused; `loadModels` is
  // replaced with a targeted RQ invalidation after inline schema creation.
  const currentProject = useProjectStore((state) => state.currentProject);
  const modelsQuery = useModels(currentProject?.project_id);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const queryClient = useQueryClient();
  const createAgentMutation = useCreateAgent(currentProject?.project_id ?? '');
  const updateAgentMutation = useUpdateAgent(currentProject?.project_id ?? '');
  // Adapter helpers preserve the legacy `(projectId, data)` /
  // `(agentId, data)` call shape used inside this file. Each helper
  // funnels into the mutation hook's `mutateAsync` so the hook owns
  // invalidation. Errors PROPAGATE -- callers rely on the outer
  // try/catch in `handleAgentFormSubmit` to surface the failure to
  // the user without closing the modal (AC-1447). Earlier versions
  // of these adapters swallowed errors and returned `null`, which
  // caused `handleAgentFormSubmit` to skip the workflow-step update
  // but still close the modal and fire a green "success" toast --
  // making creation failures look like silent successes.
  const createAgent = useCallback(
    async (_projectId: string, data: CreateAgentForm) => {
      return createAgentMutation.mutateAsync({ data });
    },
    [createAgentMutation],
  );
  const updateAgent = useCallback(
    async (agentId: number, data: CreateAgentForm) => {
      const updated = await updateAgentMutation.mutateAsync({ agentId, data });
      return { ...updated, agent_id: Number(updated.agent_id) };
    },
    [updateAgentMutation],
  );

  const [stepType, setStepType] = useState<StepType>(initialStepType || existingStep?.step_type || 'user_prompt');
  const [schemaCreationMode, setSchemaCreationMode] = useState<'select' | 'create'>('select');
  const [showInlineSchemaCreator, setShowInlineSchemaCreator] = useState(false);
  const [isCreatingSchema, setIsCreatingSchema] = useState(false);
  const [showSchemaPreview, setShowSchemaPreview] = useState(false);
  const [localModels, setLocalModels] = useState(models);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [loadingStepTypes, setLoadingStepTypes] = useState(!restrictStepType && !isEditing);
  const [formData, setFormData] = useState(() => {
    // Normalize existing conditions if editing a conditional step
    let normalizedConditions: Condition[] = [];
    if (isEditing && existingStep?.step_type === 'conditional' && existingStep?.condition?.conditions) {
      normalizedConditions = normalizeConditions(existingStep.condition.conditions);
    }

    return {
      label: existingStep?.label || existingStep?.description || '',
      description: existingStep?.description || '',
      input_message: existingStep?.input_message || '',
      step_type: existingStep?.step_type || initialStepType || 'user_prompt',
      intent: existingStep?.intent || undefined,
      agent_id: existingStep?.agent_id || null,
      // AC-1548 (epic AC-1537): step-level model. ``null`` means "no
      // override; runtime falls back to agent.model". When editing an
      // existing step we use the persisted value verbatim; for new steps
      // we seed from the agent's default once an agent is picked (handled
      // in the agent-pick handler below).
      model: (existingStep as any)?.model ?? null,
      condition: {
        conditions: normalizedConditions.length > 0 ? normalizedConditions : [],
      },
      show_output_message_items: existingStep?.show_output_message_items || false,
      on_true: existingStep?.on_true || { action: 'continue' as 'continue' },
      on_false: existingStep?.on_false || { action: 'exit' as 'exit' },
      execution_mode: existingStep?.execution_mode || 'synchronous',
      fan_out: existingStep?.fan_out || false,
      hints: existingStep?.hints || {},
      feedback_loop_hints: existingStep?.feedback_loop_hints || {},
      session_config: existingStep?.session_config || undefined,
      assistant_config: existingStep?.assistant_config || undefined,
      output_mode: (existingStep as any)?.auto_prompt_output_mode || 'text',
      populated_data: (existingStep as any)?.auto_prompt_populated_data || { text: '' },
      model_id: (existingStep as any)?.auto_prompt_model_id || null,
      config: existingStep?.config || null,
    };
  });

  // Keep localModels in sync with store
  useEffect(() => {
    setLocalModels(models);
  }, [models]);

  // Hints state
  const [showHintsSection, setShowHintsSection] = useState(false);
  // Check if this step is a target of a feedback loop
  const isTargetOfFeedbackLoop = useMemo(() => {
    if (!isEditing || !existingStep?.orchestration_step_id) {
      return false;
    }
    
    // Check all conditional nodes to see if any have a feedback loop pointing to this step
    return nodes.some(node => {
      if (node.data?.step?.step_type === 'conditional') {
        const step = node.data.step;
        const isOnTrueTarget = step.on_true?.action === 'feedback_loop' && 
          step.on_true?.next_step_id?.toString() === existingStep.orchestration_step_id?.toString();
        const isOnFalseTarget = step.on_false?.action === 'feedback_loop' && 
          step.on_false?.next_step_id?.toString() === existingStep.orchestration_step_id?.toString();
        
        return isOnTrueTarget || isOnFalseTarget;
      }
      return false;
    });
  }, [nodes, isEditing, existingStep?.orchestration_step_id]);

  const [feedbackLoopHintsEnabled, setFeedbackLoopHintsEnabled] = useState<boolean>(
    (existingStep?.feedback_loop_hints && Object.keys(existingStep.feedback_loop_hints).length > 0) ||
    false
  );
  const [feedbackLoopHints, setFeedbackLoopHints] = useState<Record<string, FeedbackLoopHint>>(() => {
    // If editing an existing step, use its hints; otherwise start empty
    if (existingStep?.feedback_loop_hints && Object.keys(existingStep.feedback_loop_hints).length > 0) {
      return existingStep.feedback_loop_hints;
    }
    return {};
  });
  
  // State for dynamically loaded default hints
  const [loadedDefaultHints, setLoadedDefaultHints] = useState<Record<string, FeedbackLoopHint> | null>(null);
  
  // Ref to track if we've already tried to load hints for this step
  const hintsLoadedRef = useRef<boolean>(false);
  
  // Function to load default hints from backend
  const loadDefaultHints = useCallback(async (stepType: string) => {
    try {
      const hints = await hintDefaultsService.getDefaultHintsWithFallback(stepType);
      setLoadedDefaultHints(hints);
      return hints;
    } catch (error) {
      console.error('Error loading default hints:', error);
      toast.error('Failed to load default hints. Using fallback values.');
      // Fallback to hardcoded defaults
      let fallbackHints = {};
      if (stepType === 'run_agent') {
        fallbackHints = DEFAULT_FEEDBACK_RECEIVER_HINTS;
      }
      setLoadedDefaultHints(fallbackHints);
      return fallbackHints;
    }
  }, []);

  // AC-1548 (epic AC-1537): track whether the user has explicitly touched
  // the step's model picker. While ``false`` (the default), changing the
  // selected agent re-seeds ``formData.model`` from the new agent's
  // default. Once the user picks a model explicitly, we stop auto-seeding
  // so their choice survives an agent swap.
  const [userPickedModel, setUserPickedModel] = useState<boolean>(
    Boolean((existingStep as any)?.model),
  );

  // Agent editing state
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [isCreatingNewAgent, setIsCreatingNewAgent] = useState(false);
  const [agentFormLoading, setAgentFormLoading] = useState(false);

  // Template selector state
  const [showCreateAgentDropdown, setShowCreateAgentDropdown] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateFormData, setTemplateFormData] = useState<Partial<CreateAgentForm> | null>(null);
  const [showExecutionModeDropdown, setShowExecutionModeDropdown] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');

  // Valid intents state for intent-aware step creation
  const [validIntents, setValidIntents] = useState<StepIntent[]>([]);
  const [isLoadingValidIntents, setIsLoadingValidIntents] = useState(false);
  const [validIntentsError, setValidIntentsError] = useState<string | null>(null);

  // AC-1296: Convert-to-Filter button state (Edit Conditional Step modal)
  const [filterConvertOpen, setFilterConvertOpen] = useState(false);
  const [filterPreview, setFilterPreview] = useState<{
    convertible: boolean;
    reason?: string;
    preview?: {
      source_field: string;
      output_field: string;
      conditions: string[];
      on_empty: 'continue' | 'exit';
      element_class_name?: string | null;
    };
  } | null>(null);
  const [filterPreviewLoading, setFilterPreviewLoading] = useState(false);
  const [filterConverting, setFilterConverting] = useState(false);
  const [filterConvertError, setFilterConvertError] = useState<string | null>(null);

  const handleOpenFilterConvert = useCallback(async () => {
    if (!existingStep?.orchestration_step_id || !existingStep?.orchestration_id) return;
    setFilterConvertOpen(true);
    setFilterConvertError(null);
    setFilterPreviewLoading(true);
    try {
      const res = await apiService.previewFilterConversion(
        existingStep.orchestration_id as string | number,
        existingStep.orchestration_step_id as string | number,
      );
      setFilterPreview(res);
    } catch (err: any) {
      setFilterConvertError(
        err?.response?.data?.detail || err?.message || 'Failed to load conversion preview'
      );
    } finally {
      setFilterPreviewLoading(false);
    }
  }, [existingStep]);

  const handleCancelFilterConvert = useCallback(() => {
    setFilterConvertOpen(false);
    setFilterConvertError(null);
    setFilterPreview(null);
  }, []);

  const handleConfirmFilterConvert = useCallback(async () => {
    if (!existingStep?.orchestration_step_id || !onConvertToFilter) return;
    if (!filterPreview?.convertible) return;
    setFilterConverting(true);
    setFilterConvertError(null);
    try {
      await onConvertToFilter(String(existingStep.orchestration_step_id));
      // WorkflowEditor.handleConvertToFilter closes this modal and reopens on the new filter step;
      // no further action needed.
    } catch (err: any) {
      setFilterConvertError(
        err?.response?.data?.detail || err?.message || 'Conversion failed. Please try again.'
      );
      setFilterConverting(false);
    }
  }, [existingStep, onConvertToFilter, filterPreview]);

  // Logic to determine the previous step for conditional builder
  const previousStepForConditional = React.useMemo(() => {
    let foundPreviousStep = null;

    if (isEditing && existingStep) {
      // If editing an existing step, find its direct predecessor
      const incomingEdge = edges.find(edge => edge.target === String(existingStep.orchestration_step_id));
      if (incomingEdge) {
        const sourceNode = nodes.find(node => node.id === incomingEdge.source);
        // Check if the direct predecessor is an agent step (with output model)
        if (sourceNode?.data?.step?.step_type === 'run_agent') {
          foundPreviousStep = sourceNode.data.step;
        }
      }
    } else if (!isEditing && previousNodeId) {
      // If adding a new step, the previousNodeId is the direct predecessor
      const sourceNode = nodes.find(node => node.id === previousNodeId);
      // Check if the direct predecessor is an agent step (with output model)
      if (sourceNode?.data?.step?.step_type === 'run_agent') {
        foundPreviousStep = sourceNode.data.step;
      }
    }
    return foundPreviousStep;
  }, [isEditing, existingStep, previousNodeId, nodes, edges]);

  // Derive upstream auto-prompt model ID for instruction assistant context (AC-1198)
  const upstreamAutoPromptModelId = React.useMemo(() => {
    let sourceNode = null;
    if (isEditing && existingStep) {
      const incomingEdge = edges.find(edge => edge.target === String(existingStep.orchestration_step_id));
      if (incomingEdge) {
        sourceNode = nodes.find(node => node.id === incomingEdge.source);
      }
    } else if (!isEditing && previousNodeId) {
      sourceNode = nodes.find(node => node.id === previousNodeId);
    }
    const prevStep = sourceNode?.data?.step;
    if (!prevStep) return undefined;

    // Case 1: preceding step is an auto_prompt with a model
    if (prevStep.step_type === 'auto_prompt' && prevStep.auto_prompt_model_id) {
      return prevStep.auto_prompt_model_id;
    }

    // Case 2: preceding step is a run_agent whose agent produces structured output
    if (prevStep.step_type === 'run_agent' && prevStep.agent_id) {
      const prevAgent = agents.find(a => a.agent_id === prevStep.agent_id);
      if (prevAgent?.output_type === 'model' && prevAgent.output_model_id) {
        return prevAgent.output_model_id;
      }
    }

    return undefined;
  }, [isEditing, existingStep, previousNodeId, nodes, edges, agents]);

  // Function to refresh workflow step data
  const refreshWorkflowStepData = useCallback(async () => {
    if (!existingStep?.orchestration_step_id) return;
    
    try {
      // Update the node data with the fresh agent data from the store
      setNodes(prevNodes => 
        prevNodes.map(node => {
          if (node.data?.step?.orchestration_step_id === existingStep.orchestration_step_id) {
            // Find the updated agent data from the store
            const updatedAgent = agents.find(agent => agent.agent_id === existingStep.agent_id);
            
            if (updatedAgent) {
              return {
                ...node,
                data: {
                  ...node.data,
                  agent: updatedAgent, // Update with fresh agent data
                  step: {
                    ...node.data.step,
                    // Update the step description if it was using the agent name
                    description: updatedAgent.name
                  },
                  // Update the label to use the agent name for agent-based steps
                  label: updatedAgent.name
                }
              };
            }
          }
          return node;
        })
      );
    } catch (error) {
      console.error('Error refreshing workflow step data:', error);
    }
  }, [existingStep?.orchestration_step_id, existingStep?.agent_id, agents, setNodes]);

  // Fetch allowed step types on mount to determine if we should show the selector
  useEffect(() => {
    if (restrictStepType && initialStepType && loadingStepTypes) {
      if (stepType !== initialStepType) {
        setStepType(initialStepType);
        setFormData((prev) => ({ ...prev, step_type: initialStepType }));
      }
      setLoadingStepTypes(false);
    } else if (!isEditing && !restrictStepType && loadingStepTypes) {
      const fetchAndDecide = async () => {
        try {
          // Get previous step info from the node
          let prevStepTypeValue: StepType | undefined = prevStepType;
          let prevIntentValue: StepIntent | undefined;
          let orchestrationIdValue: number | undefined;

          if (previousNodeId) {
            const prevNode = nodes.find(node => node.id === previousNodeId);
            if (prevNode?.data?.step) {
              prevStepTypeValue = prevNode.data.step.step_type;
              prevIntentValue = prevNode.data.step.intent;
              const oid = prevNode.data.step.orchestration_id;
              if (oid != null) orchestrationIdValue = Number(oid);
            }
          }

          const allowedOptions = await apiService.getAllowedIntents({
            prev_step_type: prevStepTypeValue,
            prev_intent: prevIntentValue,
            orchestration_id: orchestrationIdValue,
          });

          // Check if only run_agent step type is allowed
          const uniqueStepTypes = Array.from(new Set(allowedOptions.map(opt => opt.step_type)));

          if (uniqueStepTypes.length === 1 && uniqueStepTypes[0] === 'run_agent') {
            // Only run_agent is allowed, skip the selector and go directly to configuration
            setStepType('run_agent');
            setFormData((prev) => ({ ...prev, step_type: 'run_agent' }));
            setShowTypeSelector(false);
          } else {
            // Multiple step types available, show the selector
            setShowTypeSelector(true);
          }
        } catch (error) {
          console.error('Error fetching allowed step types:', error);
          // On error, show the selector as fallback
          setShowTypeSelector(true);
        } finally {
          setLoadingStepTypes(false);
        }
      };

      fetchAndDecide();
    }
  }, [restrictStepType, initialStepType, isEditing, loadingStepTypes, stepType, previousNodeId, prevStepType, nodes]);

  // Fetch valid intents when stepType is run_agent (both create and edit modes)
  useEffect(() => {
    if (stepType === 'run_agent') {
      const fetchValidIntents = async () => {
        try {
          setIsLoadingValidIntents(true);
          setValidIntentsError(null);

          // Get the previous step information
          let prevStepTypeValue: StepType | undefined = prevStepType;
          let prevIntentValue: StepIntent | undefined;
          let orchestrationIdValue: number | undefined;

          if (isEditing && existingStep) {
            // When editing, find the predecessor via incoming edges
            const stepNodeId = String(existingStep.orchestration_step_id);
            const incomingEdge = edges.find(edge => edge.target === stepNodeId);
            if (incomingEdge) {
              const sourceNode = nodes.find(node => node.id === incomingEdge.source);
              if (sourceNode?.data?.step) {
                prevStepTypeValue = sourceNode.data.step.step_type;
                prevIntentValue = sourceNode.data.step.intent;
              }
            }
            const oid = existingStep.orchestration_id;
            if (oid != null) orchestrationIdValue = Number(oid);
          } else if (previousNodeId) {
            // When creating, use the previousNodeId prop
            const prevNode = nodes.find(node => node.id === previousNodeId);
            if (prevNode?.data?.step) {
              prevStepTypeValue = prevNode.data.step.step_type;
              prevIntentValue = prevNode.data.step.intent;
              const oid = prevNode.data.step.orchestration_id;
              if (oid != null) orchestrationIdValue = Number(oid);
            }
          }

          console.debug('[StepConfigurationModal] Fetching valid intents for:', {
            prevStepType: prevStepTypeValue,
            prevIntent: prevIntentValue,
            isEditing,
            orchestrationId: orchestrationIdValue,
          });

          const allowedOptions = await apiService.getAllowedIntents({
            prev_step_type: prevStepTypeValue,
            prev_intent: prevIntentValue,
            orchestration_id: orchestrationIdValue,
          });

          // Extract intents for run_agent steps only
          const runAgentIntents = allowedOptions
            .filter(option => option.step_type === 'run_agent' && option.intent)
            .map(option => option.intent!)
            .filter((intent, index, self) => self.indexOf(intent) === index); // Remove duplicates

          console.debug('[StepConfigurationModal] Valid intents for run_agent:', runAgentIntents);
          setValidIntents(runAgentIntents);

          // If there's only one valid intent and no intent is selected, auto-select it
          if (runAgentIntents.length === 1 && !formData.intent) {
            setFormData(prev => ({ ...prev, intent: runAgentIntents[0] }));
          }

        } catch (error) {
          console.error('[StepConfigurationModal] Error fetching valid intents:', error);
          if (error && typeof error === 'object' && 'message' in error) {
            console.error('[StepConfigurationModal] Error message:', (error as any).message);
          }
          if (error && typeof error === 'object' && 'details' in error) {
            console.error('[StepConfigurationModal] Error details:', JSON.stringify((error as any).details, null, 2));
            if (Array.isArray((error as any).details?.detail)) {
              console.error('[StepConfigurationModal] Validation errors:');
              (error as any).details.detail.forEach((err: any, idx: number) => {
                console.error(`  [${idx}]:`, JSON.stringify(err, null, 2));
              });
            }
          }
          setValidIntentsError('Failed to load valid intents');
          // Fallback to all intents if API fails
          setValidIntents(['create', 'enhance', 'validate', 'conditional', 'assistant']);
        } finally {
          setIsLoadingValidIntents(false);
        }
      };

      fetchValidIntents();
    }
  // Only depend on actual inputs that should trigger a re-fetch.
  // Do NOT include outputs (validIntents, isLoadingValidIntents, validIntentsError) as deps -
  // that causes an infinite re-render loop (effect sets state -> state changes -> effect fires again).
  // Also exclude nodes/edges - these are unstable refs from Zustand that change every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepType, prevStepType, previousNodeId, isEditing, existingStep]);

  // Effect to enable feedback loop hints for steps that are targets of feedback loops
  useEffect(() => {
    if (isTargetOfFeedbackLoop && stepType === 'run_agent') {
      // Automatically enable feedback loop hints for run_agent steps when they're feedback targets
      if (!feedbackLoopHintsEnabled) {
        setFeedbackLoopHintsEnabled(true);
        // Auto-show the hints section so users can see the configuration
        setShowHintsSection(true);
      }
      // Load and set default feedback receiver hints if no custom hints are already configured
      const currentHintsEmpty = Object.keys(feedbackLoopHints).length === 0;
      if (currentHintsEmpty) {
        loadDefaultHints(stepType).then((hints) => {
          setFeedbackLoopHints({ ...hints });
        });
      }
    }
  }, [isTargetOfFeedbackLoop, stepType, feedbackLoopHintsEnabled, feedbackLoopHints, loadDefaultHints]);

  // Effect to load hints when we detect a feedback loop target
  useEffect(() => {
    if (isTargetOfFeedbackLoop && stepType === 'run_agent') {
      // Check if hints are empty and we haven't already tried to load them
      const hintsEmpty = Object.keys(feedbackLoopHints).length === 0;

      if (hintsEmpty && !hintsLoadedRef.current) {
        hintsLoadedRef.current = true;

        loadDefaultHints(stepType).then((hints) => {
          setFeedbackLoopHints({ ...hints });
          setFeedbackLoopHintsEnabled(true);
          setShowHintsSection(true);
        }).catch((error) => {
          console.error('Failed to load hints:', error);
          toast.error('Failed to load feedback loop hints. Default values will be used.');
          hintsLoadedRef.current = false; // Allow retry
        });
      }
    }
  }, [isTargetOfFeedbackLoop, stepType, existingStep?.orchestration_step_id, feedbackLoopHints, loadDefaultHints]);

  // Reset the ref when modal opens/closes or step changes
  useEffect(() => {
    hintsLoadedRef.current = false;
  }, [isOpen, existingStep?.orchestration_step_id]);

  // Effect to initialize conditions if stepType is conditional and there are available fields
  useEffect(() => {
    if (stepType === 'conditional' && !isEditing) {
      // Re-evaluate available fields here, similar to ConditionBuilder
      const getAvailableFields = () => {
        if (!previousStepForConditional) return {};
        
        if (previousStepForConditional.step_type === 'run_agent') {
          const previousNode = nodes.find(node => node.id === String(previousStepForConditional.orchestration_step_id));
          const agent = previousNode?.data?.agent;
          
          if (agent?.output_model?.properties) {
            return agent.output_model.properties;
          }
        }
        
        if (previousStepForConditional.output_model?.properties) {
          return previousStepForConditional.output_model.properties;
        }
        
        return {};
      };

      const currentAvailableFields = getAvailableFields();

      if (Object.keys(currentAvailableFields).length > 0 && formData.condition?.conditions.length === 0) {
        setFormData(prev => ({
          ...prev,
          condition: {
            conditions: [{ field: '', operator: '', value: '' }],
          },
        }));
      }
    }
  }, [stepType, isEditing, previousStepForConditional, nodes, formData.condition?.conditions.length]);

  // Check if this step type requires an agent
  const isAgentBasedStep = stepType === 'run_agent';

  // Get the selected agent for display
  const selectedAgent = agents.find(agent => agent.agent_id === formData.agent_id);

  // ----------------------------------------------------------------------
  // AC-1549 (epic AC-1537): underpowered-model complexity warning. Moved
  // from `AgentFormModal` because step.model now drives runtime selection
  // and the warning's effective-model lookup must be step-level, not
  // agent-level. The heuristic is unchanged (`evaluateAgentComplexity`).
  //
  // Effective model: ``formData.model OR selectedAgent.model`` -- matches
  // the AC-1539 resolver's fallback chain on the backend.
  //
  // The banner only fires when:
  //   - the step is a run_agent step
  //   - an agent is selected
  //   - the effective model is in the llmCatalog with tier='light'
  //   - the bound agent's signals score isComplex (>=3 signals)
  //   - the user hasn't dismissed the banner in this session
  // ----------------------------------------------------------------------
  // Pull the catalog through the same selector pattern used elsewhere in
  // this component (line 154 reads currentProject through the store).
  // Some test mocks return the whole store object regardless of the
  // selector, so coerce to an array defensively.
  const llmCatalogForWarningRaw = useProjectStore((s: any) => s.llmCatalog);
  const llmCatalogForWarning: any[] = Array.isArray(llmCatalogForWarningRaw)
    ? llmCatalogForWarningRaw
    : [];
  const [complexityWarningDismissed, setComplexityWarningDismissed] = useState(false);

  const effectiveModelForWarning = useMemo(() => {
    const stepModel = (formData as any).model;
    if (stepModel) return stepModel as string;
    if (selectedAgent?.model) return selectedAgent.model;
    return 'gpt-4o-mini';
  }, [formData, selectedAgent]);

  const stepModelTierForWarning = useMemo(() => {
    const entry = (llmCatalogForWarning || []).find(
      (e: any) => e.id === effectiveModelForWarning,
    );
    return entry?.tier ?? 'light';
  }, [llmCatalogForWarning, effectiveModelForWarning]);

  const agentComplexityForWarning = useMemo(
    () =>
      evaluateAgentComplexity({
        instructions: selectedAgent?.instructions,
        tools: selectedAgent?.tools,
        handoff_agents: (selectedAgent as any)?.handoff_agents,
        output_schema: (selectedAgent as any)?.output_schema,
        output_model_id: (selectedAgent as any)?.output_model_id,
        output_guardrails: (selectedAgent as any)?.output_guardrails,
      }),
    [selectedAgent],
  );

  const showStepComplexityWarning =
    stepType === 'run_agent' &&
    !!selectedAgent &&
    stepModelTierForWarning === 'light' &&
    agentComplexityForWarning.isComplex &&
    !complexityWarningDismissed;

  // Force refresh of selectedAgent when agents list changes
  useEffect(() => {
    if (formData.agent_id) {
      const updatedAgent = agents.find(agent => agent.agent_id === formData.agent_id);
      if (updatedAgent && (!selectedAgent || selectedAgent.agent_id !== updatedAgent.agent_id)) {
        // The agent data has been updated, we can trigger a re-render if needed
        // This is mainly for debugging - the selectedAgent should automatically update
      }
    }
  }, [agents, formData.agent_id, selectedAgent]);

  // Refresh workflow step data when agents list changes
  useEffect(() => {
    if (existingStep?.agent_id && agents.length > 0) {
      refreshWorkflowStepData();
    }
  }, [agents, existingStep?.agent_id, refreshWorkflowStepData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.create-agent-dropdown-container')) {
        setShowCreateAgentDropdown(false);
      }
    };

    if (showCreateAgentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCreateAgentDropdown]);

  // Agent editing functions
  const handleEditAgent = () => {
    const selectedAgent = agents.find(agent => agent.agent_id === formData.agent_id);
    if (selectedAgent) {
      setEditingAgent(selectedAgent);
      setIsCreatingNewAgent(false);
      setShowAgentForm(true);
    }
  };

  const handleCreateNewAgent = () => {
    setEditingAgent(null);
    setIsCreatingNewAgent(true);
    setTemplateFormData(null); // Clear any template data
    setShowAgentForm(true);
    setShowCreateAgentDropdown(false);
  };

  const handleCreateFromTemplate = () => {
    setShowTemplateSelector(true);
    setShowCreateAgentDropdown(false);
  };

  const handleTemplateSelect = async (template: AgentTemplate) => {
    if (!currentProject) return;

    setAgentFormLoading(true);
    setShowTemplateSelector(false);
    try {
      // Auto-create the agent from the template
      const agentData: CreateAgentForm = {
        name: template.name,
        instructions: template.instructions || '',
        model: template.model,
        model_settings: template.model_settings || {},
        output_type: (template.output_type as OutputType) || 'str',
        tools: template.tool_configurations?.map(tool => ({
          type: tool.type as any,
          name: tool.name,
          description: tool.description,
          ...(tool.mcp_server_id != null && { mcp_server_id: tool.mcp_server_id }),
          ...(tool.mcp_tool_name && { mcp_tool_name: tool.mcp_tool_name }),
        })) || []
      };

      const newAgent = await createAgent(currentProject.project_id, agentData);
      if (newAgent) {
        setFormData(prev => ({ ...prev, agent_id: newAgent.agent_id }));
        toast.success(`Agent "${newAgent.name}" created from template`);
      } else {
        toast.error('Failed to create agent from template.');
      }
    } catch (err) {
      console.error('Error creating agent from template:', err);
      toast.error('Failed to create agent from template. Please try again.');
    } finally {
      setAgentFormLoading(false);
    }
  };

  const handleAgentFormSubmit = async (agentData: CreateAgentForm) => {
    if (!currentProject) return;

    console.log('[StepConfigurationModal] handleAgentFormSubmit called with:', {
      agentData,
      editingAgent,
      isCreatingNewAgent,
      currentProjectId: currentProject.project_id
    });

    console.log('[StepConfigurationModal] Starting agent form submission');
    console.log('[StepConfigurationModal] Agent data to save:', agentData);
    console.log('[StepConfigurationModal] Editing agent:', editingAgent);
    console.log('[StepConfigurationModal] Is creating new agent:', isCreatingNewAgent);

    setAgentFormLoading(true);
    try {
      let newAgent: Agent | null = null;
      if (isCreatingNewAgent) {
        // Create new agent using store function
        console.log('[StepConfigurationModal] Creating new agent...');
        newAgent = await createAgent(currentProject.project_id, agentData);
        console.log('[StepConfigurationModal] New agent created:', newAgent);
      } else if (editingAgent) {
        // Update existing agent using store function
        console.log('[StepConfigurationModal] Updating existing agent...');
        newAgent = await updateAgent(editingAgent.agent_id, agentData);
        console.log('[StepConfigurationModal] Agent updated:', newAgent);
        
        // Update the workflow step data to reflect the updated agent
        if (newAgent && existingStep) {
          console.log('[StepConfigurationModal] Updating workflow step data...');
          setNodes(prevNodes => 
            prevNodes.map(node => {
              if (node.data?.step?.orchestration_step_id === existingStep.orchestration_step_id) {
                console.log('[StepConfigurationModal] Found node to update:', node.id);
                return {
                  ...node,
                  data: {
                    ...node.data,
                    agent: newAgent, // Update the agent data in the node
                    step: {
                      ...node.data.step,
                      // Update the step description if it was using the agent name
                      description: newAgent!.name
                    }
                  }
                };
              }
              return node;
            })
          );
          
          // Also refresh the workflow step data to ensure consistency
          await refreshWorkflowStepData();
        }
      }

      // Update the form with the new/updated agent
      if (newAgent) {
        console.log('[StepConfigurationModal] Updating form data with new agent ID:', newAgent.agent_id);
        setFormData(prev => ({ ...prev, agent_id: newAgent!.agent_id }));
      }

      console.log('[StepConfigurationModal] Closing agent form modal and resetting state');
      setShowAgentForm(false);
      setEditingAgent(null);
      setIsCreatingNewAgent(false);
      toast.success(isCreatingNewAgent ? 'Agent created successfully' : 'Agent updated successfully');
    } catch (error: any) {
      // AC-1447: surface the backend's actual error detail so the user can
      // see WHY creation failed (e.g., invalid tool type, schema validation
      // failure). Critically, we do NOT close the modal here -- leaving it
      // open lets the user fix the offending field and retry without losing
      // their entered values. Earlier versions closed the modal in the
      // success path of `handleAgentFormSubmit` regardless of outcome.
      console.error('Error saving agent:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to save agent. Please try again.';
      const action = isCreatingNewAgent ? 'create' : 'update';
      toast.error(
        typeof detail === 'string'
          ? `Failed to ${action} agent: ${detail}`
          : `Failed to ${action} agent. Please try again.`
      );
    } finally {
      setAgentFormLoading(false);
    }
  };

  const handleAgentFormClose = () => {
    console.log('[StepConfigurationModal] handleAgentFormClose called');
    setShowAgentForm(false);
    setEditingAgent(null);
    setIsCreatingNewAgent(false);
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('[StepConfigurationModal] Form submission started');
    console.log('  Raw form data:', formData);

    let conditionStrings: string[] = []; // Declare here

    // Require intent selection for run_agent step type
    if (stepType === 'run_agent' && !formData.intent) {
      toast.error('Please select a step intent to complete your configuration');
      return;
    }

    // Require agent selection for run_agent step type
    if (stepType === 'run_agent' && !formData.agent_id) {
      // Focus on agent selection section with smooth scroll and highlight
      const agentSelectElement = document.querySelector('[data-agent-select]');
      if (agentSelectElement) {
        agentSelectElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a gentle visual indicator
        agentSelectElement.classList.add('ring-2', 'ring-blue-400', 'ring-opacity-50', 'transition-all', 'duration-300');
        setTimeout(() => {
          agentSelectElement.classList.remove('ring-2', 'ring-blue-400', 'ring-opacity-50', 'transition-all', 'duration-300');
        }, 3000);
      }
      toast.error('Please select an agent to complete your step configuration');
      return;
    }
    
    // Validate conditional step conditions
    if (stepType === 'conditional') {
      const conditions = formData.condition?.conditions || [];
      // Convert structured conditions to string array for validation and backend
      conditionStrings = conditions.map(c => constructConditionString(c)).filter(Boolean);

      console.log('  Condition strings:', conditionStrings);
      if (!conditionStrings.length || conditionStrings.some(c => !c?.trim())) {
        alert('Conditional steps require at least one non-empty condition');
        return;
      }
    }

    // Validate queue_read step configuration
    if (stepType === 'queue_read') {
      const cfg = formData.config || {};
      if (!cfg.source_queue_id) {
        toast.error('Source queue is required for queue read steps');
        return;
      }
      if (cfg.consumption_mode === 'batch') {
        const bs = cfg.batch_size;
        if (typeof bs === 'number' && (bs < 1 || bs > 500)) {
          toast.error('Batch size must be between 1 and 500');
          return;
        }
      }
      const vt = cfg.visibility_timeout_seconds;
      if (typeof vt === 'number' && (vt < 60 || vt > 3600)) {
        toast.error('Visibility timeout must be between 60 and 3600 seconds');
        return;
      }
    }

    // Validate queue_write step configuration
    if (stepType === 'queue_write') {
      const cfg = formData.config || {};
      if (!cfg.target_queue_id) {
        toast.error('Target queue is required for queue write steps');
        return;
      }
      if (cfg.data_source === 'specific_step' && !cfg.source_step_id) {
        toast.error('Source step is required when Data Source is "Specific Step"');
        return;
      }
      if (cfg.data_source === 'specific_field' && !(cfg.source_field || '').trim()) {
        toast.error('Source field is required when Data Source is "Specific Field from Previous Step"');
        return;
      }
    }

    // For agent-based steps, use the agent name as the description
    // For user_prompt steps, preserve both label and description separately
    let description: string;
    if (isAgentBasedStep && selectedAgent) {
      description = selectedAgent.name;
    } else if (stepType === 'user_prompt') {
      // For user_prompt steps, use the description field as entered by user
      // The label will be used for display purposes in the UI
      description = formData.description || '';
    } else {
      description = formData.description || '';
    }
    
    // When editing, only send the fields that can be modified
    const baseEditData = {
      description: description,
      input_message: formData.input_message,
      agent_id: formData.agent_id,
      intent: formData.intent, // Explicitly include intent
      // AC-1548 (epic AC-1537): step-level model override. Only meaningful
      // for run_agent steps; included for run_agent and left ``undefined``
      // for other types so the backend doesn't write a stray value.
      model: stepType === 'run_agent' ? (formData as any).model ?? null : undefined,
      condition: stepType === 'conditional' ? {
        conditions: conditionStrings.length > 0 ? conditionStrings : []
      } : formData.condition,
      show_output_message_items: formData.show_output_message_items,
      on_true: stepType === 'conditional' ? formData.on_true : undefined,
      on_false: stepType === 'conditional' ? formData.on_false : undefined,
      execution_mode: formData.execution_mode,
      fan_out: formData.fan_out,
      label: formData.label, // Include label for display purposes
      feedback_loop_hints: feedbackLoopHintsEnabled ? feedbackLoopHints : {}, // Include hints only if enabled
      session_config: formData.session_config, // Include session configuration
      auto_prompt_output_mode: stepType === 'auto_prompt' ? formData.output_mode : undefined,
      auto_prompt_populated_data: stepType === 'auto_prompt' ? formData.populated_data : undefined,
      auto_prompt_model_id: stepType === 'auto_prompt' ? formData.model_id : undefined,
      config: (stepType === 'queue_read' || stepType === 'queue_write' || stepType === 'filter' || stepType === 'rdap_lookup') ? formData.config : undefined,
    };

    // Convert display step type to API step type for backward compatibility
    const apiStepType = getApiStepType(stepType || 'user_prompt');
    const stepIntent = formData.intent || getStepIntent(stepType || 'user_prompt');

    const baseCreateData = {
      ...formData,
      description: description,
      step_type: apiStepType,
      intent: stepIntent, // Use determined intent
      // AC-1548 (epic AC-1537): step-level model override on create. The
      // ``...formData`` spread already carries ``model`` for run_agent
      // steps; we explicitly null it out for non-run_agent types so the
      // backend doesn't persist a stray value on user_prompt / conditional
      // / etc.
      model: stepType === 'run_agent' ? (formData as any).model ?? null : undefined,
      condition: stepType === 'conditional' ? {
        conditions: conditionStrings.length > 0 ? conditionStrings : []
      } : formData.condition,
      on_true: stepType === 'conditional' ? formData.on_true : undefined,
      on_false: stepType === 'conditional' ? formData.on_false : undefined,
      label: formData.label, // Include label for display purposes
      feedback_loop_hints: feedbackLoopHintsEnabled ? feedbackLoopHints : {}, // Include hints only if enabled
      session_config: formData.session_config, // Include session configuration
      auto_prompt_output_mode: stepType === 'auto_prompt' ? formData.output_mode : undefined,
      auto_prompt_populated_data: stepType === 'auto_prompt' ? formData.populated_data : undefined,
      auto_prompt_model_id: stepType === 'auto_prompt' ? formData.model_id : undefined,
      config: (stepType === 'queue_read' || stepType === 'queue_write' || stepType === 'filter' || stepType === 'rdap_lookup') ? formData.config : undefined,
    };

    // Add assistant_config for assistant intent steps
    console.log('[StepConfigurationModal] Checking intent for assistant config:', stepIntent);
    if (stepIntent === 'assistant') {
      console.log('[StepConfigurationModal] Adding assistant_config for assistant intent');
      const existingAssistantConfig = formData.assistant_config || {};
      
      const assistantConfig = {
        auto_loop: existingAssistantConfig.auto_loop ?? true,
        context_window: existingAssistantConfig.context_window ?? 5
      };

      console.log('[StepConfigurationModal] Created assistant_config:', assistantConfig);

      if (isEditing) {
        (baseEditData as any).assistant_config = assistantConfig;
      } else {
        (baseCreateData as any).assistant_config = assistantConfig;
      }
    }

    const dataToSubmit = isEditing ? baseEditData : baseCreateData;

    console.log('[StepConfigurationModal] Submitting step data:', dataToSubmit);
    console.log('[StepConfigurationModal] Session config in submission:', {
      hasAssistantConfig: !!(dataToSubmit as any).assistant_config,
      sessionConfig: (dataToSubmit as any).session_config,
      sessionEnabled: (dataToSubmit as any).session_config?.enabled,
      sessionMode: (dataToSubmit as any).session_config?.mode
    });

    // Tutorial steps 12 and 14 are advanced by TutorialOverlay via click trigger on btn-submit-step

    onSave(dataToSubmit);
    onClose();
  };

  const handleStepTypeSelect = (selectedType: StepType) => {
    setStepType(selectedType);
    setFormData((prev) => ({ ...prev, step_type: selectedType }));
    setShowTypeSelector(false);
  };

  const getModalTitle = () => {
    if (stepType) {
      let stepTypeName = stepType.replace('run_', '');
      const formattedStepName = stepTypeName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      if (isEditing) {
        return `Edit ${formattedStepName} Step`;
      } else {
        return `Configure ${formattedStepName} Step`;
      }
    }
    return isEditing ? 'Edit Step' : 'Configure Step';
  };

  const renderStepTypeFields = () => {
    switch (stepType) {
      case 'user_prompt':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Input Message
            </label>
            <textarea
              value={formData.input_message}
              onChange={(e) => setFormData({ ...formData, input_message: e.target.value })}
              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              rows={4}
              placeholder="Enter the message to show to the user"
            />
          </div>
        );

      case 'auto_prompt':
        return (
          <div className="space-y-4">
            {/* Output Mode Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Output Mode
              </label>
              <div className="flex space-x-4">
                <Tooltip content="Simple text data for basic use cases">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="output_mode"
                    value="text"
                    checked={formData.output_mode === 'text'}
                    onChange={(e) => setFormData({ ...formData, output_mode: 'text' })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span>Text</span>
                </label>
                </Tooltip>
                <Tooltip content="Structured data with validated schema">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="output_mode"
                    value="model"
                    checked={formData.output_mode === 'model'}
                    onChange={(e) => setFormData({ ...formData, output_mode: 'model' })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span>Schema</span>
                </label>
                </Tooltip>
              </div>
            </div>

            {/* Help text for output mode */}
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {formData.output_mode === 'text' && (
                <p>Text mode: Provide simple text that will be passed to downstream steps. Use this for instructions, prompts, or simple data.</p>
              )}
              {formData.output_mode === 'model' && (
                <p>Schema mode: Provide structured data matching a schema definition. Use this for complex data with multiple fields, arrays, or nested objects.</p>
              )}
            </div>

            {/* Text Mode: Textarea */}
            {formData.output_mode === 'text' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Text Data
                </label>
                <textarea
                  value={formData.populated_data?.text || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    populated_data: { text: e.target.value }
                  })}
                  rows={8}
                  placeholder="Enter text data that will be passed to downstream steps (e.g., instructions, prompts, or simple data)"
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This text will be available to downstream agents as context. You can use this to provide instructions, data, or prompts.
                </p>
              </div>
            )}

            {/* Schema Mode: Schema Selector/Creator + SchemaForm */}
            {formData.output_mode === 'model' && (
              <>
                {/* Schema Selection Controls */}
                <div className="mb-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSchemaCreationMode('select');
                        setShowInlineSchemaCreator(false);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        schemaCreationMode === 'select'
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-primary-600'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      Choose Existing Schema
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSchemaCreationMode('create');
                        setShowInlineSchemaCreator(true);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        schemaCreationMode === 'create'
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-primary-600'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      Create New Schema
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {schemaCreationMode === 'select'
                      ? 'Select from existing schemas to define the output structure'
                      : 'Define a new schema for the auto prompt output'
                    }
                  </div>
                </div>

                {/* Select Mode */}
                {schemaCreationMode === 'select' && (
                  <div>
                    {formData.model_id && (
                      <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between">
                        <p className="text-sm text-green-700 dark:text-green-400">
                          <span className="font-medium">Selected Schema:</span> {
                            localModels.find(m => m.model_id === formData.model_id)?.class_name || 'Loading...'
                          }
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowSchemaPreview(prev => !prev)}
                          className="ml-2 inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-200"
                          aria-label={showSchemaPreview ? 'Hide schema' : 'View schema'}
                          aria-expanded={showSchemaPreview}
                        >
                          {showSchemaPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          {showSchemaPreview ? 'Hide schema' : 'View schema'}
                        </button>
                      </div>
                    )}
                    <CustomSelect
                      value={formData.model_id ? String(formData.model_id) : ''}
                      onChange={(value) => {
                        const modelId = parseInt(value);
                        setFormData({
                          ...formData,
                          model_id: modelId,
                          populated_data: {}
                        });
                      }}
                      options={localModels.map(model => ({
                        value: String(model.model_id),
                        label: model.class_name,
                      }))}
                      placeholder="-- Select a schema --"
                      aria-label="Select Schema"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Schemas define the structure of your data with typed fields. Select a schema and fill in the required fields below.
                    </p>
                    {showSchemaPreview && formData.model_id && (() => {
                      const previewModel = localModels.find(m => m.model_id === formData.model_id);
                      if (!previewModel) return null;
                      return (
                        <div className="mt-4">
                          <ModelSchemaPreview
                            model={previewModel}
                            allModels={localModels}
                            onClose={() => setShowSchemaPreview(false)}
                          />
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Create Mode */}
                {schemaCreationMode === 'create' && (
                  <div>
                    <InlineModelCreator
                      existingModels={localModels}
                      onModelCreated={(newModel) => {
                        setLocalModels(prev => [...prev, newModel]);
                        setFormData(prev => ({
                          ...prev,
                          model_id: newModel.model_id,
                          populated_data: {},
                        }));
                        setSchemaCreationMode('select');
                        setShowInlineSchemaCreator(false);
                        setIsCreatingSchema(false);
                        if (currentProject?.project_id) {
                          // AC-1412 / AC-1377 P5.1: refresh the project's
                          // models cache via React Query invalidation
                          // instead of the legacy loadModels() store
                          // method. The RQ subscription rebuilds the list
                          // automatically.
                          queryClient.invalidateQueries({
                            queryKey: queryKeys.projectModels(currentProject.project_id),
                          });
                        }
                        toast.success(`Schema '${newModel.class_name}' created and selected successfully!`);
                      }}
                      onCancel={() => {
                        setSchemaCreationMode('select');
                        setShowInlineSchemaCreator(false);
                        setIsCreatingSchema(false);
                      }}
                      isExpanded={showInlineSchemaCreator}
                      onToggleExpanded={() => setShowInlineSchemaCreator(!showInlineSchemaCreator)}
                      isCreating={isCreatingSchema}
                      projectId={currentProject?.project_id}
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Create a new schema for this step's output format. The schema will be automatically selected once created.
                    </p>
                  </div>
                )}

                {/* SchemaForm - renders when model selected */}
                {formData.model_id && (() => {
                  const selectedModel = localModels.find(m => m.model_id === formData.model_id);
                  if (!selectedModel) return null;

                  const transformedSchema = transformModelProperties(
                    selectedModel.properties,
                    localModels
                  );

                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Schema Configuration
                      </label>
                      <SchemaForm
                        schema={transformedSchema}
                        value={formData.populated_data || {}}
                        onChange={(newData) => setFormData({
                          ...formData,
                          populated_data: newData
                        })}
                      />
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        );

      case 'conditional':
        // For user-friendly step type names
        const stepTypeConfig: Record<StepType, { name: string }> = {
          run_agent: { name: 'Agent' },
          user_prompt: { name: 'User Prompt' },
          conditional: { name: 'Conditional' },
          auto_prompt: { name: 'Auto Prompt' },
          queue_write: { name: 'Queue Write' },
          queue_read: { name: 'Queue Read' },
          filter: { name: 'Filter' },
          rdap_lookup: { name: 'Domain Availability' },
        };
        // Find the index of the current step (if editing)
        let currentStepIndex = -1;
        if (isEditing && existingStep) {
          currentStepIndex = nodes.findIndex(node => node.data?.step?.orchestration_step_id === existingStep.orchestration_step_id);
        } else if (!isEditing && previousNodeId) {
          currentStepIndex = nodes.findIndex(node => node.id === previousNodeId);
        }
        // Allow previous steps of type 'run_agent' for feedback loops
        const availableSteps = nodes
          .filter((node, idx) =>
            node.data?.step &&
            node.data.step.orchestration_step_id !== undefined &&
            node.data.step.step_type === 'run_agent' &&
            (currentStepIndex === -1 || idx < currentStepIndex)
          )
          .map(node => {
            const step = node.data.step;
            const typeName = stepTypeConfig[step.step_type as StepType]?.name || step.step_type;
            let label = '';
            if (step.agent_id) {
              const agent = agents.find(a => a.agent_id === step.agent_id);
              if (agent) {
                label = `${agent.name} (${typeName}${step.description ? ': ' + step.description : ''})`;
              } else {
                label = `${typeName}${step.description ? ': ' + step.description : ''}`;
              }
            } else if (step.label) {
              label = `${step.label} (${typeName})`;
            } else if (step.description) {
              label = `${typeName}: ${step.description}`;
            } else {
              label = typeName;
            }
            return {
              id: step.orchestration_step_id,
              label,
            };
          });
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Conditions
              </label>
              <ConditionBuilder
                conditions={formData.condition?.conditions || []}
                onChange={(newConditions: Condition[]) =>
                  setFormData({
                    ...formData,
                    condition: { conditions: newConditions }
                  })
                }
                previousStep={previousStepForConditional}
                stepId={existingStep?.orchestration_step_id as number | undefined}
                workflowId={existingStep?.orchestration_id as number | undefined}
                onConvertToFilter={
                  onConvertToFilter
                    ? (stepId: number) => onConvertToFilter(String(stepId))
                    : undefined
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  On True Action
                </label>
                <CustomSelect
                  value={formData.on_true?.action || 'continue'}
                  onChange={(value) => setFormData(prev => ({
                    ...prev,
                    on_true: { ...prev.on_true, action: value as 'continue' | 'exit' | 'feedback_loop' }
                  }))}
                  options={[
                    { value: 'continue', label: 'Continue' },
                    { value: 'exit', label: 'Exit' },
                    { value: 'feedback_loop', label: 'Feedback Loop' },
                  ]}
                />
                {formData.on_true?.action === 'feedback_loop' && (
                  <div className="mt-2 space-y-2 p-2 border rounded bg-gray-50 dark:bg-gray-900">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Next Step</label>
                    <CustomSelect
                      value={formData.on_true?.next_step_id || ''}
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        on_true: { ...prev.on_true, next_step_id: value }
                      }))}
                      options={availableSteps.map(step => ({
                        value: step.id,
                        label: step.label,
                      }))}
                      placeholder="Select step..."
                    />
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Feedback Variable</label>
                    <CustomSelect
                      value={formData.on_true?.feedback || ''}
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        on_true: { ...prev.on_true, feedback: value }
                      }))}
                      options={feedbackFields.map(field => ({
                        value: field,
                        label: field,
                      }))}
                      placeholder="Select feedback field..."
                    />
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Max Attempts
                      <Tooltip content="How many times to retry the feedback loop before exiting.">
                      <span className="text-xs text-gray-400 ml-1">
                        (How many times to retry before exit)
                      </span>
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      className="input w-full"
                      placeholder="Max attempts (optional)"
                      min={1}
                      value={formData.on_true?.max_attempts || ''}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        on_true: { ...prev.on_true, max_attempts: e.target.value ? Number(e.target.value) : undefined }
                      }))}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  On False Action
                </label>
                <CustomSelect
                  value={formData.on_false?.action || 'exit'}
                  onChange={(value) => setFormData(prev => ({
                    ...prev,
                    on_false: { ...prev.on_false, action: value as 'continue' | 'exit' | 'feedback_loop' }
                  }))}
                  options={[
                    { value: 'continue', label: 'Continue' },
                    { value: 'exit', label: 'Exit' },
                    { value: 'feedback_loop', label: 'Feedback Loop' },
                  ]}
                />
                {formData.on_false?.action === 'feedback_loop' && (
                  <div className="mt-2 space-y-2 p-2 border rounded bg-gray-50 dark:bg-gray-900">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Next Step</label>
                    <CustomSelect
                      value={formData.on_false?.next_step_id || ''}
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        on_false: { ...prev.on_false, next_step_id: value }
                      }))}
                      options={availableSteps.map(step => ({
                        value: step.id,
                        label: step.label,
                      }))}
                      placeholder="Select step..."
                    />
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Feedback Variable</label>
                    <CustomSelect
                      value={formData.on_false?.feedback || ''}
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        on_false: { ...prev.on_false, feedback: value }
                      }))}
                      options={feedbackFields.map(field => ({
                        value: field,
                        label: field,
                      }))}
                      placeholder="Select feedback field..."
                    />
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Score Variable (Optional)
                      <Tooltip content="The field containing the score to track across iterations">
                      <span className="text-xs text-gray-400 ml-1">
                        (For tracking scoring progression)
                      </span>
                      </Tooltip>
                    </label>
                    <CustomSelect
                      value={formData.on_false?.score_variable || ''}
                      onChange={(value) => setFormData(prev => ({
                        ...prev,
                        on_false: { ...prev.on_false, score_variable: value || undefined }
                      }))}
                      options={[
                        { value: '', label: 'No score tracking...' },
                        ...feedbackFields.map(field => ({
                          value: field,
                          label: field,
                        })),
                      ]}
                      placeholder="No score tracking..."
                    />
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Max Attempts
                      <Tooltip content="How many times to retry the feedback loop before exiting.">
                      <span className="text-xs text-gray-400 ml-1">
                        (How many times to retry before exit)
                      </span>
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      className="input w-full"
                      placeholder="Max attempts (optional)"
                      min={1}
                      value={formData.on_false?.max_attempts || ''}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        on_false: { ...prev.on_false, max_attempts: e.target.value ? Number(e.target.value) : undefined }
                      }))}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'queue_read':
        return (
          <QueueReadStepConfig
            step={{
              ...existingStep,
              step_type: 'queue_read',
              config: formData.config || existingStep?.config,
            } as WorkflowStep}
            projectId={currentProject?.project_id || ''}
            onConfigChange={(config) => {
              setFormData((prev) => ({ ...prev, config }));
            }}
            disabled={false}
          />
        );

      case 'queue_write':
        return (
          <QueueWriteStepConfig
            step={{
              ...existingStep,
              step_type: 'queue_write',
              config: formData.config || existingStep?.config,
            } as WorkflowStep}
            projectId={currentProject?.project_id || ''}
            onConfigChange={(config) => {
              setFormData((prev) => ({ ...prev, config }));
            }}
            disabled={false}
          />
        );

      case 'filter':
        // AC-1273 / AC-1279 / AC-1297 / AC-1299: filter step config. Resolve the
        // upstream through the edge graph (previousStepForConditional memo) and
        // ATTACH the agent object from the upstream node's sibling data. The
        // flow store keeps step and agent as siblings under node.data; without
        // merging, FilterStepConfig.getListFields returns empty and the source
        // field dropdown shows "No list[X] fields found".
        return (
          <FilterStepConfig
            config={(formData.config as any) || (existingStep?.config as any) || {}}
            onChange={(config) => {
              setFormData((prev) => ({ ...prev, config }));
            }}
            previousStep={resolveUpstreamForIntrospection({
              isEditing,
              existingStep,
              previousNodeId,
              nodes,
              edges,
              fallback: previousStepForConditional || (existingStep as any)?.previous_step,
            })}
            workflowId={existingStep?.orchestration_id as any}
            // AC-1358: Thread the active project's id through so the registry
            // model picker can populate. currentProject.project_id may be a
            // string in some store paths; coerce to number for the API.
            projectId={
              currentProject?.project_id !== undefined && currentProject?.project_id !== null
                ? Number(currentProject.project_id)
                : undefined
            }
          />
        );

      case 'rdap_lookup':
        // AC-1444 / AC-1451 / AC-1453 (epic AC-1434): RDAP domain availability
        // step. The panel emits a complete config via onChange and the modal
        // owns persistence. RDAPStepConfig now introspects the upstream step's
        // output to populate the source-field dropdown, so we MUST resolve the
        // direct predecessor here without restricting to run_agent (the way
        // previousStepForConditional does). queue_read upstreams are common
        // for RDAP -- if filtered out, the dropdown is empty.
        return (
          <RDAPStepConfig
            config={(formData.config as any) || (existingStep?.config as any) || {}}
            onChange={(config) => {
              setFormData((prev) => ({ ...prev, config }));
            }}
            previousStep={resolveUpstreamForIntrospection({
              isEditing,
              existingStep,
              previousNodeId,
              nodes,
              edges,
              fallback: previousStepForConditional || (existingStep as any)?.previous_step,
            })}
            workflowId={
              existingStep?.orchestration_id !== undefined && existingStep?.orchestration_id !== null
                ? Number(existingStep.orchestration_id)
                : undefined
            }
            // AC-1488: Thread the active project's id through so the release
            // registry-model picker can populate via
            // apiService.getRegistryEnabledModels. currentProject.project_id
            // may be a string in some store paths; coerce to number for the
            // API (mirrors the FilterStepConfig wiring above).
            projectId={
              currentProject?.project_id !== undefined && currentProject?.project_id !== null
                ? Number(currentProject.project_id)
                : undefined
            }
          />
        );

      default:
        const selectedAgent = agents.find(agent => agent.agent_id === formData.agent_id);

        if (stepType === 'run_agent') {
          return (
            <div className="space-y-4">
              {/* Agent Selection */}
              <div data-agent-select>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Agent <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-grow">
                    <button
                      type="button"
                      onClick={() => { if (!agentFormLoading) { setShowAgentDropdown(!showAgentDropdown); setAgentSearchQuery(''); } }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${agentFormLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={formData.agent_id ? '' : 'text-gray-400'}>
                        {formData.agent_id
                          ? agents.find(a => a.agent_id === formData.agent_id)?.name || 'Select an agent...'
                          : 'Select an agent...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAgentDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showAgentDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                        {agents.length > 5 && (
                          <div className="sticky top-0 bg-white dark:bg-gray-800 p-2 border-b border-gray-100 dark:border-gray-700">
                            <input
                              type="text"
                              value={agentSearchQuery}
                              onChange={(e) => setAgentSearchQuery(e.target.value)}
                              placeholder="Search agents..."
                              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-warm-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                              autoFocus
                            />
                          </div>
                        )}
                        {agents
                          .filter(a => !agentSearchQuery || a.name.toLowerCase().includes(agentSearchQuery.toLowerCase()))
                          .map((agent) => (
                            <button
                              key={agent.agent_id}
                              type="button"
                              onClick={() => {
                                // AC-1548: seed step.model from the agent's
                                // default UNLESS the user has explicitly
                                // picked a step-level model already. The
                                // existing-step edit case has
                                // ``userPickedModel === true`` so the
                                // override survives an agent swap.
                                setFormData((prev) => ({
                                  ...prev,
                                  agent_id: agent.agent_id,
                                  model: userPickedModel
                                    ? prev.model
                                    : agent.model ?? prev.model,
                                }));
                                setShowAgentDropdown(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 ${
                                formData.agent_id === agent.agent_id
                                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{agent.name}</span>
                                {formData.agent_id === agent.agent_id && (
                                  <Check className="w-4 h-4 text-primary-600" />
                                )}
                              </div>
                            </button>
                          ))}
                        {agents.filter(a => !agentSearchQuery || a.name.toLowerCase().includes(agentSearchQuery.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No agents found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="relative create-agent-dropdown-container">
                    <button
                      type="button"
                      data-tutorial="btn-create-agent-inline"
                      onClick={() => setShowCreateAgentDropdown(!showCreateAgentDropdown)}
                      disabled={agentFormLoading}
                      className="flex-shrink-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Create new agent"
                    >
                      <Tooltip content="Create new agent">
                      <Plus className="w-5 h-5" />
                      </Tooltip>
                    </button>

                    {/* Dropdown Menu */}
                    {showCreateAgentDropdown && (
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-cap-dark ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1" role="menu">
                          <button
                            onClick={handleCreateNewAgent}
                            data-tutorial="btn-create-agent-from-scratch-inline"
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white"
                            role="menuitem"
                          >
                            <Plus className="w-4 h-4 mr-3" />
                            Create from Scratch
                          </button>
                          <button
                            onClick={handleCreateFromTemplate}
                            data-tutorial="btn-use-template-inline"
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white"
                            role="menuitem"
                          >
                            <Sparkles className="w-4 h-4 mr-3" />
                            Use Template
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {agentFormLoading && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Updating agent list...</p>
                )}
              </div>

              {/* Agent Details Preview & Edit Button */}
              {selectedAgent && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white">Agent Details</h4>
                    <button
                      type="button"
                      onClick={handleEditAgent}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit Agent</span>
                    </button>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="font-medium">Name:</span> {selectedAgent.name}
                    </div>
                    <div>
                      <span className="font-medium">Model:</span> {selectedAgent.model}
                    </div>
                    {selectedAgent.instructions && (
                      <div>
                        <span className="font-medium">Instructions:</span>
                        <p className="mt-1 text-gray-500 dark:text-gray-400 prose prose-sm max-w-none">
                          {selectedAgent.instructions}
                        </p>
                      </div>
                    )}
                    {/* Tools Section */}
                    {selectedAgent.tools && selectedAgent.tools.length > 0 && (
                      <div className="mt-4">
                        <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Attached Tools ({selectedAgent.tools.length})</h5>
                        <div className="space-y-2">
                          {(() => {
                            // Group MCP tools by server, render non-MCP tools individually
                            const mcpGroups = new Map<string, { serverName: string; tools: typeof selectedAgent.tools }>();
                            const nonMcpTools: typeof selectedAgent.tools = [];

                            selectedAgent.tools.forEach((tool) => {
                              if (tool.type === 'mcp') {
                                // Use mcp_server_id if available, otherwise group by parsed server name
                                const serverName = tool.name?.split(': ')[0] || 'MCP Server';
                                const groupKey = tool.mcp_server_id != null ? String(tool.mcp_server_id) : `name:${serverName}`;
                                const group = mcpGroups.get(groupKey) || {
                                  serverName,
                                  tools: [],
                                };
                                group.tools.push(tool);
                                mcpGroups.set(groupKey, group);
                              } else {
                                nonMcpTools.push(tool);
                              }
                            });

                            return (
                              <>
                                {Array.from(mcpGroups.entries()).map(([serverId, group]) => (
                                  <details key={`mcp-${serverId}`} className="group border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-900">
                                    <summary className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 list-none [&::-webkit-details-marker]:hidden">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className="w-3.5 h-3.5 text-gray-500 transition-transform group-open:rotate-90" />
                                        <span className="font-semibold">{group.serverName}</span>
                                      </div>
                                      <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 rounded">
                                        {group.tools.length} {group.tools.length === 1 ? 'tool' : 'tools'}
                                      </span>
                                    </summary>
                                    <div className="px-3 pb-2 pt-1 border-t border-gray-200 dark:border-gray-700 space-y-1">
                                      {group.tools.map((tool, idx) => (
                                        <div key={idx} className="text-xs text-gray-700 dark:text-gray-300 py-1 px-2">
                                          {tool.mcp_tool_name || tool.name}
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                ))}
                                {nonMcpTools.map((tool, idx) => (
                                  <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded px-3 py-2 bg-gray-50 dark:bg-gray-900">
                                    <div className="flex items-center justify-between">
                                      <span className="font-semibold">{tool.name || tool.type}</span>
                                      <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 rounded">{tool.type}</span>
                                    </div>
                                    {tool.description && (
                                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{tool.description}</div>
                                    )}
                                    {tool.user_location && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Location: {tool.user_location.city}, {tool.user_location.region}, {tool.user_location.country}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AC-1548 (epic AC-1537): step-level model picker. Always
                  rendered for run_agent steps. Seeds from the selected
                  agent's default when an agent is picked (see agent-pick
                  handler above); the user can override per-step. The
                  runtime resolves ``effective_model = step.model OR
                  agent.model`` via the AC-1539 resolver, so leaving the
                  picker at the agent default is a no-op. */}
              <div data-testid="step-model-picker">
                <ModelPickerField
                  value={
                    (formData as any).model ??
                    selectedAgent?.model ??
                    'gpt-4o-mini'
                  }
                  onChange={(value) => {
                    setUserPickedModel(true);
                    setFormData((prev) => ({ ...prev, model: value }));
                  }}
                  label="Model"
                  helpText="The model the runtime uses for this step. Pre-populates from the selected agent's default; choose another model to override for this step only."
                />
              </div>

              {/* No Agents Available */}
              {agents.length === 0 && !agentFormLoading && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-2">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        No agents available
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          You need to create an agent first. Click the "+" button to create one.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Execution Mode Configuration */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Execution Mode
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowExecutionModeDropdown(!showExecutionModeDropdown)}
                      onBlur={() => setTimeout(() => setShowExecutionModeDropdown(false), 150)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <span>{(formData.execution_mode || 'synchronous') === 'synchronous' ? 'Synchronous' : 'Parallel: Fan-out'}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showExecutionModeDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showExecutionModeDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
                        {[
                          { value: 'synchronous', label: 'Synchronous', desc: 'Steps run one after another in sequence.' },
                          { value: 'parallel', label: 'Parallel: Fan-out', desc: 'Agent processes multiple items in parallel for better performance.' }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              const newMode = option.value as 'synchronous' | 'parallel';
                              setFormData({
                                ...formData,
                                execution_mode: newMode,
                                fan_out: newMode === 'parallel'
                              });
                              setShowExecutionModeDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 first:rounded-t-md last:rounded-b-md ${
                              (formData.execution_mode || 'synchronous') === option.value
                                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{option.label}</span>
                              {(formData.execution_mode || 'synchronous') === option.value && (
                                <Check className="w-4 h-4 text-primary-600" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{option.desc}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          );
        }
        return null;
    }
  };

  // Get available fields for feedback variable dropdown (with dot notation and nested model support, matching ConditionBuilder)
  let feedbackFields: string[] = [];
  let feedbackClassName = '';
  // Use the same flattenProperties logic as ConditionBuilder
  const getFeedbackFields = (previousStepForConditional: any, nodes: any[], models: any[]): { fields: string[]; className: string } => {
    if (!previousStepForConditional) return { fields: [], className: '' };
    let rootProperties: Record<string, any> = {};
    let className = '';
    // Try to get agent output model properties first
    if (previousStepForConditional.step_type === 'run_agent') {
      const previousNode = nodes.find(node => node.id === String(previousStepForConditional.orchestration_step_id));
      const agent = previousNode?.data?.agent;
      if (agent?.output_model?.properties) {
        rootProperties = agent.output_model.properties;
        className = agent.output_model.class_name || '';
      }
    }
    // Fallback to step's output model
    if (Object.keys(rootProperties).length === 0 && previousStepForConditional.output_model?.properties) {
      rootProperties = previousStepForConditional.output_model.properties;
      className = previousStepForConditional.output_model.class_name || '';
    }
    // Use models from the store for model reference resolution
    const flattenProperties = (properties: Record<string, any>, prefix: string = '', depth: number = 0): string[] => {
      let fields: string[] = [];
      for (const [key, value] of Object.entries(properties)) {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        const isModelReference = (
          key.endsWith('_type') ||
          key.endsWith('_model') ||
          key.endsWith('_ref') ||
          (value && typeof value === 'object' && value.type === 'object') ||
          (value && typeof value === 'object' && typeof value.type === 'string' && value.type.includes('Type')) ||
          (typeof value === 'string' && value.includes('Type'))
        );
        if (isModelReference) {
          if (value && typeof value === 'object' && value.properties) {
            fields = fields.concat(flattenProperties(value.properties, fullPath, depth + 1));
          } else {
            // Try to find the model in the project store
            let foundModel = null;
            let modelClassName: string | null = null;
            if (typeof value === 'string') {
              modelClassName = value;
            } else if (value && typeof value === 'object' && typeof value.type === 'string') {
              modelClassName = value.type;
            } else if (value && typeof value === 'object' && value.class_name) {
              modelClassName = value.class_name;
            }
            if (modelClassName) {
              modelClassName = modelClassName.replace(/[()]/g, '').trim();
              const found = models?.find(model => model.class_name === modelClassName);
              if (found && found.properties) {
                fields = fields.concat(flattenProperties(found.properties, fullPath, depth + 1));
              } else if (modelClassName === 'GenreType') {
                // Special case for GenreType
                fields.push(`${fullPath}.name`);
                fields.push(`${fullPath}.description`);
              } else {
                // Fallback: Add common model fields
                const commonFields = [
                  'name', 'description', 'id', 'value', 'label', 'code', 'is_active', 'created_at', 'updated_at'
                ];
                for (const field of commonFields) {
                  fields.push(`${fullPath}.${field}`);
                }
              }
            }
          }
          continue;
        }
        fields.push(fullPath);
        if (value && typeof value === 'object' && value.properties) {
          fields = fields.concat(flattenProperties(value.properties, fullPath, depth + 1));
        }
      }
      return fields;
    };
    return { fields: flattenProperties(rootProperties), className };
  };
  const { fields: feedbackFieldOptions, className: feedbackClass } = getFeedbackFields(previousStepForConditional, nodes, models);
  feedbackFields = feedbackFieldOptions;
  feedbackClassName = feedbackClass;
  if (feedbackClassName) {
    feedbackFields = feedbackFields.map(f => `${feedbackClassName}.${f}`);
  }

  // Show loading state while determining which modal to show
  if (loadingStepTypes) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-cap-dark rounded-lg p-4 sm:p-6 w-full max-w-[600px] mx-4 sm:mx-0">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (showTypeSelector) {
    // Get previous step info from the node
    let prevStepTypeValue: StepType | undefined = prevStepType;
    let prevIntentValue: StepIntent | undefined;

    let orchestrationIdValue: number | undefined;
    if (previousNodeId) {
      const prevNode = nodes.find(node => node.id === previousNodeId);
      if (prevNode?.data?.step) {
        prevStepTypeValue = prevNode.data.step.step_type;
        prevIntentValue = prevNode.data.step.intent;
        const oid = prevNode.data.step.orchestration_id;
        if (oid != null) orchestrationIdValue = Number(oid);
      }
    }
    if (orchestrationIdValue == null && existingStep?.orchestration_id != null) {
      orchestrationIdValue = Number(existingStep.orchestration_id);
    }

    return (
      <IntentAwareStepSelector
        onSelect={(selectedStepType: StepType, selectedIntent?: StepIntent) => {
          setStepType(selectedStepType);
          setFormData((prev) => ({
            ...prev,
            step_type: selectedStepType,
            intent: selectedIntent
          }));
          setShowTypeSelector(false);
        }}
        onClose={onClose}
        prevStepType={prevStepTypeValue}
        prevIntent={prevIntentValue}
        orchestrationId={orchestrationIdValue}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-modal-active="true">
      <div className="bg-white dark:bg-cap-dark rounded-lg p-4 sm:p-6 w-full max-w-[800px] max-h-[80vh] overflow-y-auto mx-4 sm:mx-0" data-tutorial="modal-step-config">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">
              {getModalTitle()}
            </h2>
            <ContextualHelpIcon
              docPath="how-to/build-workflow.md"
              tooltip="Learn about workflows"
              size="sm"
            />
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* AC-1549 (epic AC-1537): underpowered-model warning banner.
            Moved here from AgentFormModal so it reads the EFFECTIVE step
            model (step.model fallback agent.model). Rendered above the
            form, like the previous agent-edit version. */}
        {showStepComplexityWarning && (
          <div
            data-testid="step-complexity-warning-banner"
            role="alert"
            aria-live="polite"
            className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
          >
            <div className="flex items-start space-x-2">
              <AlertTriangle
                aria-hidden="true"
                className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  This model may be underpowered for this agent.
                </p>
                <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                  Based on the agent's instructions, tools, and structured
                  output, it is fairly complex. {effectiveModelForWarning}{' '}
                  often hits turn limits or times out on agents like this.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setUserPickedModel(true);
                      setFormData((prev) => ({ ...prev, model: 'gpt-4o' }));
                    }}
                    className="px-3 py-1 text-xs font-medium bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                  >
                    Switch to GPT-4o
                  </button>
                  <button
                    type="button"
                    onClick={() => setComplexityWarningDismissed(true)}
                    className="px-3 py-1 text-xs font-medium bg-white dark:bg-gray-800 text-yellow-800 dark:text-yellow-200 border border-yellow-300 dark:border-yellow-700 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/40 transition-colors"
                  >
                    Keep {effectiveModelForWarning}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Step Type
            </label>
            <div
              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 bg-gray-100 text-left text-gray-500 dark:text-gray-400 cursor-not-allowed sm:text-sm"
              tabIndex={-1}
              aria-disabled="true"
            >
              {stepType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </div>
          </div>

          {stepType === 'run_agent' && (
            <StepIntentSelector
              selectedIntent={formData.intent}
              onSelect={(intent) => {
                console.log('[StepConfigurationModal] Setting intent in formData:', intent);
                const newFormData = { ...formData, intent };
                console.log('[StepConfigurationModal] New formData with intent:', newFormData);
                setFormData(newFormData);
              }}
              validIntents={validIntents.length > 0 ? validIntents : undefined}
              isLoading={isLoadingValidIntents}
              prevStepType={prevStepType}
            />
          )}

          {/* Show validation error if intent fetching failed */}
          {stepType === 'run_agent' && validIntentsError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-2">
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-300">Intent Validation Error</h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1">{validIntentsError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Label field - only shown for non-agent steps (agent steps use agent name automatically) */}
          {!isAgentBasedStep && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Label
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder={stepType === 'user_prompt' ? "Enter a descriptive label for this prompt step (e.g., 'Get user input', 'Ask for preferences')" : "Enter step label"}
              />
              {stepType === 'user_prompt' && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  This label will be used to identify this step in the workflow flow.
                </p>
              )}
            </div>
          )}

          {!isAgentBasedStep && stepType !== 'user_prompt' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                rows={3}
                placeholder="Enter step description"
              />
            </div>
          )}

          {renderStepTypeFields()}

          {/* Session Configuration Section */}
          {isAgentBasedStep && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <SessionConfiguration
                config={formData.session_config}
                onChange={(config) => setFormData({ ...formData, session_config: config })}
                stepType={stepType}
                intent={formData.intent}
              />
            </div>
          )}

          {/* Hints Configuration Section - Only show for run_agent steps */}
          {stepType === 'run_agent' && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={feedbackLoopHintsEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setFeedbackLoopHintsEnabled(enabled);
                    if (enabled && stepType === 'run_agent' && Object.keys(feedbackLoopHints).length === 0) {
                      // Load and set default hints when enabling for run_agent steps
                      loadDefaultHints(stepType).then((hints) => {
                        setFeedbackLoopHints({ ...hints });
                      });
                    }
                  }}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                <span>Feedback Loop Hints</span>
                {isTargetOfFeedbackLoop && stepType === 'run_agent' && (
                  <span className="ml-2 text-xs text-green-600 font-medium">
                    (Auto-enabled: Step receives feedback)
                  </span>
                )}
              </label>
              {feedbackLoopHintsEnabled && (
                <div className="flex items-center space-x-2">
                  {stepType === 'run_agent' && (
                    <button
                      type="button"
                      onClick={() => {
                        hintsLoadedRef.current = false; // Reset the ref to allow reload
                        loadDefaultHints(stepType).then((hints) => {
                          setFeedbackLoopHints({ ...hints });
                          // Optionally show the configuration section when resetting
                          if (!showHintsSection) {
                            setShowHintsSection(true);
                          }
                        }).catch((error) => {
                          console.error('Error in Reset to Defaults:', error);
                        });
                      }}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Reset to Defaults
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowHintsSection(!showHintsSection)}
                    className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
                  >
                    <span>{showHintsSection ? 'Hide' : 'Show'} Configuration</span>
                    {showHintsSection ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
            
            {feedbackLoopHintsEnabled && showHintsSection && (
              <div className="mt-4 space-y-6 pl-6 border-l-2 border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isTargetOfFeedbackLoop && stepType === 'run_agent' ? (
                    <>
                      This step will receive feedback from a validator. Default hints will be automatically applied to help the agent
                      use the feedback effectively. You can customize these hints below if needed.
                    </>
                  ) : (
                    'Configure hints that will be applied during feedback loops to enhance agent instructions.'
                  )}
                </p>
                
                {/* Iteration 1 Hints */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-white flex items-center">
                    <span className="bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-md mr-2">Iteration 1</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">First attempt at validation</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Enhancement Instruction</label>
                      <textarea
                        placeholder="Instructions for how the agent should evaluate the output"
                        value={feedbackLoopHints['1']?.enhancement_instruction || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '1': { ...prev['1'], enhancement_instruction: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Validation Rules</label>
                      <textarea
                        placeholder="Specific rules or criteria for validation"
                        value={feedbackLoopHints['1']?.validation_rules || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '1': { ...prev['1'], validation_rules: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Focus Areas</label>
                      <textarea
                        placeholder="Specific areas to focus on during validation"
                        value={feedbackLoopHints['1']?.focus_areas || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '1': { ...prev['1'], focus_areas: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    </div>
                </div>

                {/* Iteration 2 Hints */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-white flex items-center">
                    <span className="bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-md mr-2">Iteration 2</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">After first round of feedback</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Enhancement Instruction
                        <span className="text-gray-400 ml-1">(use {'{previous_feedback}'} for dynamic content)</span>
                      </label>
                      <textarea
                        placeholder="Instructions for evaluating improvements based on previous feedback"
                        value={feedbackLoopHints['2']?.enhancement_instruction || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '2': { ...prev['2'], enhancement_instruction: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Validation Rules</label>
                      <textarea
                        placeholder="Rules for acknowledging improvements and remaining issues"
                        value={feedbackLoopHints['2']?.validation_rules || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '2': { ...prev['2'], validation_rules: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Focus Areas
                        <span className="text-gray-400 ml-1">(use {'{previous_feedback}'} for dynamic content)</span>
                      </label>
                      <textarea
                        placeholder="Areas to check based on previous feedback"
                        value={feedbackLoopHints['2']?.focus_areas || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '2': { ...prev['2'], focus_areas: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* Iteration 3+ Hints */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-white flex items-center">
                    <span className="bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-md mr-2">Iteration 3+</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">Multiple rounds of feedback</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Enhancement Instruction
                        <span className="text-gray-400 ml-1">(use {'{iteration}'} and {'{previous_feedback}'})</span>
                      </label>
                      <textarea
                        placeholder="Instructions for later iterations, considering cumulative improvements"
                        value={feedbackLoopHints['3+']?.enhancement_instruction || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '3+': { ...prev['3+'], enhancement_instruction: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Validation Rules</label>
                      <textarea
                        placeholder="Rules emphasizing cumulative progress and essential issues only"
                        value={feedbackLoopHints['3+']?.validation_rules || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '3+': { ...prev['3+'], validation_rules: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Focus Areas
                        <span className="text-gray-400 ml-1">(use {'{previous_feedback}'} for dynamic content)</span>
                      </label>
                      <textarea
                        placeholder="Areas to verify have been addressed from previous feedback"
                        value={feedbackLoopHints['3+']?.focus_areas || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '3+': { ...prev['3+'], focus_areas: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Iteration Context (Optional)</label>
                      <textarea
                        placeholder="Additional context about expectations for later iterations"
                        value={feedbackLoopHints['3+']?.iteration_context || ''}
                        onChange={(e) => setFeedbackLoopHints(prev => ({
                          ...prev,
                          '3+': { ...prev['3+'], iteration_context: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    <span className="font-semibold">💡 Dynamic Variables:</span> Use these placeholders in your hints:
                  </p>
                  <ul className="mt-2 text-xs text-blue-700 dark:text-blue-400 space-y-1">
                    <li><code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">{'{previous_feedback}'}</code> - The feedback from the previous iteration</li>
                    <li><code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">{'{iteration}'}</code> - The current iteration number</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          )}

          <div className="flex justify-between items-center mt-6">
            {/* Convert to Auto-Prompt button - only show when editing a user_prompt step */}
            {isEditing && stepType === 'user_prompt' && existingStep?.orchestration_step_id && onConvertToAutoPrompt ? (
              <Tooltip content="Convert this user prompt step to an auto-prompt step">
              <button
                type="button"
                onClick={() => {
                  // Don't call onClose() - handleConvertToAutoPrompt manages modal state
                  // It will update the node and open the auto-prompt configuration modal
                  onConvertToAutoPrompt(String(existingStep.orchestration_step_id));
                }}
                className="flex items-center gap-2 px-4 py-2 border border-teal-300 rounded-md shadow-sm text-sm font-medium text-teal-700 bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all"
              >
                <Wand2 className="h-4 w-4" />
                Convert to Auto-Prompt
              </button>
              </Tooltip>
            ) : isEditing && stepType === 'conditional' && existingStep?.orchestration_step_id && onConvertToFilter ? (
              <Tooltip content="Convert this conditional step to a filter step that iterates the list per element">
              <button
                type="button"
                data-testid="open-convert-to-filter"
                onClick={handleOpenFilterConvert}
                className="flex items-center gap-2 px-4 py-2 border border-amber-300 dark:border-amber-700 rounded-md shadow-sm text-sm font-medium text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 hover:from-amber-100 hover:to-orange-100 dark:hover:from-amber-900/40 dark:hover:to-orange-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all"
              >
                <Wand2 className="h-4 w-4" />
                Convert to Filter
              </button>
              </Tooltip>
            ) : (
              <div /> /* Empty div to maintain flex spacing */
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-tutorial="btn-submit-step"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {isEditing ? 'Save Changes' : 'Add Step'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* AC-1296: Convert-to-Filter confirmation overlay */}
      {filterConvertOpen && (
        <div
          role="dialog"
          aria-labelledby="convert-to-filter-heading"
          data-testid="convert-to-filter-overlay"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
          onClick={(e) => {
            if (e.target === e.currentTarget && !filterConverting) handleCancelFilterConvert();
          }}
        >
          <div className="w-full max-w-md bg-white dark:bg-cap-dark rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 id="convert-to-filter-heading" className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
              Convert to Filter
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will convert the conditional step into a filter step that iterates the list per element.
            </p>

            {filterPreviewLoading && (
              <div className="text-sm text-gray-600 dark:text-gray-400 py-3">Checking convertibility&hellip;</div>
            )}

            {!filterPreviewLoading && filterPreview && !filterPreview.convertible && (
              <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3">
                <div className="font-medium mb-1">This conditional can't be converted.</div>
                <div>{filterPreview.reason || 'See the backend response for details.'}</div>
              </div>
            )}

            {!filterPreviewLoading && filterPreview?.convertible && (
              <>
                <div className="text-xs space-y-2 mb-3">
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-200">Original condition</div>
                    <div className="font-mono text-gray-600 dark:text-gray-400 break-all">
                      {(existingStep as any)?.condition?.conditions?.[0] ?? '(no condition)'}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-200">Rewritten condition</div>
                    <div className="font-mono text-gray-600 dark:text-gray-400 break-all">
                      {filterPreview.preview?.conditions?.[0] ?? '(preview unavailable)'}
                    </div>
                  </div>
                </div>

                <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-2 mb-3">
                  Non-matching elements will be dropped and only matching ones flow downstream.
                </div>
              </>
            )}

            {filterConvertError && (
              <div className="text-red-600 dark:text-red-400 text-sm mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                {filterConvertError}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              {filterPreview?.convertible && (
                <button
                  type="button"
                  data-testid="confirm-convert-to-filter"
                  onClick={handleConfirmFilterConvert}
                  disabled={filterConverting || filterPreviewLoading}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-primary-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  {filterConverting ? 'Converting…' : 'Confirm'}
                </button>
              )}
              <button
                type="button"
                onClick={handleCancelFilterConvert}
                disabled={filterConverting}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {filterPreview?.convertible ? 'Cancel' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Form Modal */}
      {showAgentForm && (
        <AgentFormModal
          agent={editingAgent}
          models={models}
          availableAgents={agents}
          templateData={templateFormData}
          stepIntent={formData.intent || undefined}
          upstreamModelId={upstreamAutoPromptModelId}
          onSubmit={handleAgentFormSubmit}
          onClose={handleAgentFormClose}
          showToast={false}
        />
      )}

      {/* Agent Template Selector Modal */}
      {showTemplateSelector && (
        <AgentTemplateSelector
          projectId={currentProject?.project_id ? String(currentProject.project_id) : undefined}
          onSelectTemplate={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}
    </div>
  );
};

// Helper function to construct condition string (copied from ConditionBuilder for use here)
const constructConditionString = (condition: Condition): string => {
  const { field, operator, value } = condition;
  if (!field || !operator) return ''; // Incomplete condition

  // Format value based on type for Python AST evaluation
  let formattedValue = value;
  if (typeof value === 'boolean') {
    formattedValue = value ? 'True' : 'False';
  } else if (typeof value === 'string') {
    // Check if it's a boolean string (from form serialization)
    if (value === 'true' || value === 'false') {
      formattedValue = value === 'true' ? 'True' : 'False';
    } else {
      formattedValue = `"${value}"`;
    }
  }

  return `${field} ${operator} ${formattedValue}`;
};

export default StepConfigurationModal; 
